import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import type { Partner } from "@shared/schema";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BadgeCheck } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { PartnerDetailView } from "./PartnerDetailView";

function getDealsCount(partner: Partner): number | null {
  const details = (partner.details as Record<string, unknown> | null) ?? {};
  return typeof details.colivingDealsCount === "number" ? details.colivingDealsCount : null;
}

// ─── All Partners Panel ────────────────────────────────────────────────────────
export function RealtorPartnersPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [revokeDialog, setRevokeDialog] = useState<{ open: boolean; partnerId: number | null; reason: string }>({ open: false, partnerId: null, reason: "" });
  const [dealsSort, setDealsSort] = useState<"none" | "asc" | "desc">("none");

  const realtorsQuery = useQuery<Partner[]>({
    queryKey: ["/api/admin/partners", "realtor"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/partners?partnerType=realtor`, { credentials: "include" });
      return res.json();
    },
  });

  const grantMutation = useMutation({
    mutationFn: async ({ partnerId, details }: { partnerId: number; details: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/admin/partners/${partnerId}`, { details }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners", vars.partnerId, "certification-events"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/realtors"] });
      toast({ title: "Certification updated" });
      setRevokeDialog({ open: false, partnerId: null, reason: "" });
    },
    onError: (err: any) => toast({ title: "Failed to update", description: parseErrorMessage(err, "Couldn't update. Please try again."), variant: "destructive" }),
  });

  const marketingVisibleMutation = useMutation({
    mutationFn: async ({ partnerId, marketingVisible }: { partnerId: number; marketingVisible: boolean }) =>
      apiRequest("PATCH", `/api/admin/partners/${partnerId}`, { marketingVisible }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/realtors"] });
      toast({ title: "Marketing visibility updated" });
    },
    onError: (err: any) => toast({ title: "Failed to update", description: parseErrorMessage(err, "Couldn't update visibility. Please try again."), variant: "destructive" }),
  });

  const allRealtors = realtorsQuery.data ?? [];
  const selectedPartner = allRealtors.find(p => p.id === selectedPartnerId) ?? null;

  const displayedRealtors = dealsSort === "none"
    ? allRealtors
    : [...allRealtors].sort((a, b) => {
        const av = getDealsCount(a) ?? -1;
        const bv = getDealsCount(b) ?? -1;
        return dealsSort === "asc" ? av - bv : bv - av;
      });

  const toggleDealsSort = () => setDealsSort(s => (s === "none" ? "desc" : s === "desc" ? "asc" : "none"));

  if (selectedPartner) {
    return <PartnerDetailView partner={selectedPartner} onBack={() => setSelectedPartnerId(null)} onUpdated={() => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/realtors"] });
    }} />;
  }

  const certifiedCount = allRealtors.filter(p => (p.details as Record<string, unknown> | null)?.colivingCertified === true).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Realtor Partners</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Award or revoke the Co-Living Certified badge that appears on the Find a Realtor page.</p>
        </div>
        <span className="text-xs text-muted-foreground">{certifiedCount} of {allRealtors.length} certified</span>
      </div>
      <Card>
        {realtorsQuery.isLoading ? (
          <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : allRealtors.length === 0 ? (
          <div className="p-12 text-center">
            <BadgeCheck className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No realtor partners yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>
                    <button
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={toggleDealsSort}
                      data-testid="button-sort-deals"
                    >
                      Deals
                      {dealsSort === "desc" ? <ArrowDown className="h-3 w-3" /> : dealsSort === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                    </button>
                  </TableHead>
                  <TableHead>Certified</TableHead>
                  <TableHead>Certified Since</TableHead>
                  <TableHead>Marketing Visible</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedRealtors.map(p => {
                  const details = (p.details as Record<string, unknown> | null) ?? {};
                  const certified = details.colivingCertified === true;
                  const certDateRaw = details.certificationDate;
                  const certDate = typeof certDateRaw === "string" ? certDateRaw : null;
                  const licenseNum = typeof details.licenseNumber === "string" ? details.licenseNumber : "";
                  const licenseState = typeof details.licenseState === "string" ? details.licenseState : "";
                  const dealsCount = typeof details.colivingDealsCount === "number" ? details.colivingDealsCount : null;
                  return (
                    <TableRow key={p.id} data-testid={`row-realtor-${p.id}`}>
                      <TableCell>
                        <button className="font-medium text-left hover:underline" onClick={() => setSelectedPartnerId(p.id)} data-testid={`link-realtor-${p.id}`}>
                          {p.contactName}
                        </button>
                        <div className="text-xs text-muted-foreground">{p.email}</div>
                      </TableCell>
                      <TableCell>{p.companyName}</TableCell>
                      <TableCell className="text-sm">{licenseNum || "—"}</TableCell>
                      <TableCell className="text-sm">{licenseState || "—"}</TableCell>
                      <TableCell className="text-sm tabular-nums" data-testid={`text-deals-${p.id}`}>{dealsCount ?? "—"}</TableCell>
                      <TableCell>
                        {certified ? (
                          <Badge className="text-xs bg-green-600" data-testid={`badge-cert-${p.id}`}><BadgeCheck className="h-3 w-3 mr-1" />Certified</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs" data-testid={`badge-cert-${p.id}`}>Not certified</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-testid={`text-cert-date-${p.id}`}>
                        {certified && certDate ? new Date(certDate).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={p.marketingVisible}
                          disabled={marketingVisibleMutation.isPending}
                          onCheckedChange={(checked) => marketingVisibleMutation.mutate({ partnerId: p.id, marketingVisible: checked })}
                          data-testid={`switch-marketing-visible-${p.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {certified ? (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600"
                            onClick={() => setRevokeDialog({ open: true, partnerId: p.id, reason: "" })}
                            data-testid={`button-revoke-cert-${p.id}`}>
                            Revoke
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-green-600"
                            disabled={grantMutation.isPending}
                            onClick={() => grantMutation.mutate({
                              partnerId: p.id,
                              details: { ...details, colivingCertified: true, certificationDate: new Date().toISOString(), certificationRevokeReason: null },
                            })}
                            data-testid={`button-grant-cert-${p.id}`}>
                            Grant
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={revokeDialog.open} onOpenChange={(open) => setRevokeDialog(d => ({ ...d, open }))}>
        <DialogContent data-testid="dialog-revoke-cert">
          <DialogHeader>
            <DialogTitle>Revoke Co-Living Certification</DialogTitle>
            <DialogDescription>This will remove the Co-Living Certified badge from this realtor. Provide a reason for your records.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason</Label>
            <Textarea rows={3} value={revokeDialog.reason}
              onChange={e => setRevokeDialog(d => ({ ...d, reason: e.target.value }))}
              placeholder="Why is the certification being revoked?"
              data-testid="input-revoke-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeDialog({ open: false, partnerId: null, reason: "" })}>Cancel</Button>
            <Button variant="destructive"
              disabled={!revokeDialog.reason.trim() || grantMutation.isPending}
              onClick={() => {
                const partner = allRealtors.find(p => p.id === revokeDialog.partnerId);
                if (!partner) return;
                const details = (partner.details as Record<string, unknown> | null) ?? {};
                grantMutation.mutate({
                  partnerId: partner.id,
                  details: { ...details, colivingCertified: false, certificationDate: null, certificationRevokeReason: revokeDialog.reason },
                });
              }}
              data-testid="button-confirm-revoke">
              Revoke Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
